<template>
  <tree-select
    ref="select"
    class="zm-select"
    v-if="tree"
    v-bind="$props"
  >
    <template #prefix>
      <slot name="prefix"></slot>
    </template>
  </tree-select>
  <filterable-select
    ref="select"
    class="zm-select"
    v-else-if="filterable"
    v-bind="[$props,$attrs]"
  >
    <template #prefix>
      <slot name="prefix"></slot>
    </template>
    <template #empty>
      <slot name="empty"></slot>
    </template>
    <template #default>
      <slot></slot>
    </template>
  </filterable-select>
  <normal-select
    v-else
    ref="select"
    class="zm-select"
    v-bind="$props"
  >
    <template #prefix>
      <slot name="prefix"></slot>
    </template>
    <template #empty>
      <slot name="empty"></slot>
    </template>
    <template #default>
      <slot></slot>
    </template>
    <template v-if="$scopedSlots.default" #default="props">
      <slot :item="props.item">
      </slot>
    </template>
  </normal-select>
</template>

<script>
  import TreeSelect from './tree-select';
  import FilterableSelect from './filterable-select';
  import NormalSelect from './normal-select';
  import { noop } from '../../utils/util';
  import '../../../i18n/select';
  import { t } from '../../locale';

  export default {
    name: 'ZmSelect',
    components: {
      TreeSelect,
      NormalSelect,
      FilterableSelect
    },
    props: {
      id: {
        type: String,
        default: ''
      },
      value: [String, Number, Array, Object, Boolean],
      ariaRequired: Boolean,
      size: {
        type: String,
        default: 'small'
      },
      disabled: Boolean,
      clearable: Boolean,
      filterable: Boolean,
      allowCreate: Boolean,
      loading: Boolean,
      hasMore: Boolean,
      remote: Boolean,
      loadingText: String,
      noMatchText: String,
      noDataText: String,
      emptyDataText: Boolean,
      remoteMethod: Function,
      filterMethod: Function,
      widthLimit: [Boolean, Object],
      multiple: Boolean,
      multipleLimit: {
        type: Number,
        default: 0
      },
      placeholder: {
        type: String,
        default() {
          return t('select.placeholder');
        }
      },
      defaultFirstOption: Boolean,
      valueKey: {
        type: String,
        default: 'value'
      },
      collapseTags: Boolean,
      popperAppendToBody: Boolean,
      inlineLabel: String,
      listLabel: String,
      listLabelBy: String,
      label: String,
      labelBy: String,
      popperOptions: {
        type: Object,
        default() {
          return {
            gpuAcceleration: false
          };
        }
      },
      popperClass: String,
      tree: {
        type: Object,
        default() {
          return null;
        }
      },
      checkboxOptions: {
        type: Array,
        default() {
          return [];
        }
      },
      selectOptions: {
        type: Array,
        default() {
          return [];
        }
      },
      selectOptionsValueKey: {
        type: String,
        default: 'value'
      },
      limitQueryLength: {
        type: Number,
        default: 0
      },
      filterLabel: Function,
      queryWhenFocus: Boolean,
      queryWhenVisible: {
        type: Boolean,
        default: true
      },
      selectedAfterFocus: Boolean,
      maxLine: {
        type: Number,
        default: 0
      },
      clearQueryWhenVisible: {
        type: Boolean,
        default: true
      },
      queryAfterValueChange: Boolean,
      checkStrictly: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        visible: false,
        query: '',
        hoverIndex: -1,
        options: []
      };
    },
    mounted() {
      this.makeProxy();
    },
    watch: {
      filterable() {
        this.$nextTick(this.makeProxy);
      }
    },
    methods: {
      makeProxy() {
        this.proxyEvents();
        this.proxyMethods();
        this.proxyData();
        this.proxyRefs();
      },
      proxyEvents() {
        const events = ['input', 'change', 'visible-change', 'remove-tag', 'focus', 'blur', 'clear'];
        const vm = this.$refs.select;
        events.forEach((event) => {
          vm.$on(event, (...args) => {
            this.$emit.apply(this, [event, ...args]);
          });
        });
      },
      proxyMethods() {
        const methods = ['focus', 'blur', 'handleESC', 'handleQueryChange', 'toggleMenu', 'onScrollEnd'];
        methods.forEach((method) => {
          const vm = this.$refs.select;
          if (vm[method]) {
            this[method] = vm[method].bind(vm);
          } else {
            this[method] = noop;
          }
        });
      },
      proxyData() {
        const vm = this.$refs.select;
        Object.keys(this._data).forEach((key) => {
          vm.$watch(key, (newVal) => {
            this[key] = newVal;
          }, { immediate: true });
        });

        Object.keys(this._data).forEach((key) => {
          this.$watch(key, (newVal) => {
            vm[key] = newVal;
          });
        });
      },
      proxyRefs() {
        const vm = this.$refs.select;
        const refs = ['referenceInner'];
        refs.forEach((ref) => {
          this.$refs[ref] = vm.$refs[ref];
        });
      }
    }
  };
</script>

<style lang="scss">
  @import "./style";
</style>
